/**
 * `fetch` for backend calls — attaches the Entra id token as a bearer and the
 * pre-shared API token as `X-API-TOKEN`.
 *
 * An explicit wrapper rather than a patched global `fetch`, so the tokens can
 * only ever travel to the backend and never to a third-party host.
 */
import { acquireIdToken } from '../auth/msalToken';
import { getApiToken } from './apiToken';

/**
 * Every shape `HeadersInit` allows has to be normalised before the spread below:
 * spreading a Headers instance yields `{}`, which would silently drop the
 * caller's Content-Type rather than fail.
 */
const toRecord = (headers: HeadersInit | undefined): Record<string, string> => {
  if (!headers) return {};
  if (headers instanceof Headers) {
    // forEach rather than entries(): the DOM.Iterable lib isn't enabled here.
    const record: Record<string, string> = {};
    headers.forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }
  if (Array.isArray(headers)) return Object.fromEntries(headers);
  return { ...headers };
};

/**
 * Merged into a plain record instead of a Headers instance because every call
 * site passes an object literal, and that keeps their header casing intact.
 */
const withAuthHeaders = (init: RequestInit, bearer: string | null): RequestInit => ({
  ...init,
  headers: {
    ...toRecord(init.headers),
    'X-API-TOKEN': getApiToken(),
    ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
  },
});

export async function authFetch(
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const bearer = await acquireIdToken();
  return fetch(input, withAuthHeaders(init, bearer));
}
